import React, { lazy } from "react";
import { Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import usePermissions from "src/utils/usePermissions";

import Loadable from "../layouts/full/shared/loadable/Loadable";

/*   Layouts   */

const FullLayout = Loadable(
  lazy(() => import("../layouts/full/FullLayout"))
);

const BlankLayout = Loadable(
  lazy(() => import("../layouts/blank/BlankLayout"))
);

/*   Dashboard   */

const Dashboard = Loadable(
  lazy(() => import("../views/dashboard/Dashboard"))
);

/*   Authentication   */

const Login = Loadable(
  lazy(() => import("../views/authentication/login/Login"))
);

const PrivateRouteError = Loadable(
  lazy(() => import("../views/apps/Error/PrivateRouteError"))
);

/*   Employee Module   */

const Employees = Loadable(
  lazy(() => import("../views/apps/employees/Employees"))
);

/*   Corporation Module   */

const Corporation = Loadable(
  lazy(() => import("../views/apps/corporation/Corporation"))
);

/*   Shop Module   */

const Shop = Loadable(
  lazy(() => import("../views/apps/shop/Shop"))
);

/*   Country Module   */

const Country = Loadable(
  lazy(() => import("../views/apps/country/Country"))
);

/*   State Module   */

const State = Loadable(
  lazy(() => import("../views/apps/state/State"))
);

/*   City Module   */

const City = Loadable(
  lazy(() => import("../views/apps/city/City"))
);

/*   Designation Module   */

const Designation = Loadable(
  lazy(() => import("../views/apps/designation/Designation"))
);

/*   Department Module   */

const Department = Loadable(
  lazy(() => import("../views/apps/department/Department"))
);

/*   Site Module   */

const Site = Loadable(
  lazy(() => import("../views/apps/site/Site"))
);

/*   Site Department Module   */

const SiteDepartment = Loadable(
  lazy(() => import("../views/apps/siteDepartment/SiteDepartment"))
);

/*   Particular Module   */

const Particular = Loadable(
  lazy(() => import("../views/apps/particular/Particular"))
);

/*   Category Module   */

const Category = Loadable(
  lazy(() => import("../views/apps/category/Category"))
);

const SubCategory = Loadable(
  lazy(() => import("../views/apps/subCategory/SubCategory"))
);

/*   Vehicle Type Module   */

// const VehicleType = Loadable(
//   lazy(() => import("../views/apps/vehicle-type/VehicleType"))
// );

/*   Plan Module   */

const Plans = Loadable(
  lazy(() => import("../views/apps/plan/Plans"))
);

const PlanDetails = Loadable(
  lazy(() => import("../views/apps/plan/PlanDetails"))
);

/*   Attendance Module   */

const Attendance = Loadable(
  lazy(() => import("../views/apps/attendance/Attendance"))
);

/*   TADA Module   */

const Tada = Loadable(
  lazy(() => import("../views/apps/tada/Tada"))
);

/*   User Management   */

const Users = Loadable(
  lazy(() => import("../views/apps/users/Users"))
);

const Roles = Loadable(
  lazy(() => import("../views/apps/roles/Roles"))
);

const Permissions = Loadable(
  lazy(() => import("../views/apps/permissions/Permissions"))
);

/*   Contact Us   */

const ContactUs = Loadable(
  lazy(() => import("../views/apps/contact-us/ContactUs"))
);

/*   Privacy Policy   */

const PrivacyPolicy = Loadable(
  lazy(() => import("../views/pages/privacy-policy/PrivacyPolicy"))
);

/*   Protected Route   */

const ProtectedRoute = ({ element, permission }) => {
  const { hasPermission } = usePermissions();
  if (permission && !hasPermission(permission)) {
    if (permission !== 'dashboard.read' && hasPermission('dashboard.read')) {
      return <Navigate to="/dashboard" replace />;
    }
    if (hasPermission('pjp.read')) {
      return <Navigate to="/plan" replace />;
    }
    if (hasPermission('attendance.read')) {
      return <Navigate to="/attendance" replace />;
    }
    return <Navigate to="/auth/404" replace />;
  }
  return element;
};

/*   Private Routes   */
const privateRoute = {
  path: "/",
  element: <FullLayout />,

  children: [

    /*   Default Redirect   */
    {
      path: "/",
      element: (
        <Navigate
          to="/dashboard"
          replace
        />
      ),
    },

    /*   Dashboard   */

    {
      path: "/dashboard",
      element: <ProtectedRoute element={<Dashboard />} permission="dashboard.read" />,
    },

    /*   Employee   */
    {
      path: "/employees",
      element: (
        <ProtectedRoute
          element={<Employees />}
          permission="users.read"
        />
      ),
    },

    /*   Corporation   */
    {
      path: "/corporation",
      element: (
        <ProtectedRoute
          element={<Corporation />}
          permission="corporation.read"
        />
      ),
    },

    /*   Shop   */
    {
      path: "/shop",
      element: (
        <ProtectedRoute
          element={<Shop />}
          permission="shops.read"
        />
      ),
    },

    /*   Country   */
    {
      path: "/country",
      element: (
        <ProtectedRoute
          element={<Country />}
          permission="country.read"
        />
      ),
    },

    /*   State   */
    {
      path: "/state",
      element: (
        <ProtectedRoute
          element={<State />}
          permission="state.read"
        />
      ),
    },

    /*   City   */
    {
      path: "/city",
      element: (
        <ProtectedRoute
          element={<City />}
          permission="city.read"
        />
      ),
    },

    /*   Designation   */
    {
      path: "/designation",
      element: (
        <ProtectedRoute
          element={<Designation />}
        />
      ),
    },

    /*   Department   */
    {
      path: "/department",
      element: (
        <ProtectedRoute
          element={<Department />}
        />
      ),
    },

    /*   Site   */
    {
      path: "/site",
      element: (
        <ProtectedRoute
          element={<Site />}
        />
      ),
    },

    /*   Site Department   */
    {
      path: "/site-department",
      element: (
        <ProtectedRoute
          element={<SiteDepartment />}
        />
      ),
    },

    /*   Particular   */
    {
      path: "/particular",
      element: (
        <ProtectedRoute
          element={<Particular />}
        />
      ),
    },

    /*   Category   */
    {
      path: "/category",
      element: (
        <ProtectedRoute
          element={<Category />}
          permission="category.read"
        />
      ),
    },
    {
      path: "/sub-category",
      element: (
        <ProtectedRoute
          element={<SubCategory />}
          permission="category.read"
        />
      ),
    },

    /*   Plan   */
    {
      path: "/plan",
      element: (
        <ProtectedRoute
          element={<Plans />}
          permission="pjp.read"
        />
      ),
    },

    {
      path: "/plan/:planId",
      element: (
        <ProtectedRoute
          element={<PlanDetails />}
          permission="pjp.read"
        />
      ),
    },

    /*   Attendance   */
    {
      path: "/attendance",
      element: (
        <ProtectedRoute
          element={<Attendance />}
          permission="attendance.read"
        />
      ),
    },

    /*   TADA   */
    {
      path: "/tada",
      element: (
        <ProtectedRoute
          element={<Tada />}
        />
      ),
    },
    {
      path: "/ta-da",
      element: (
        <Navigate
          to="/tada"
          replace
        />
      ),
    },

    /*   User Management   */
    {
      path: "/users",
      element: (
        <ProtectedRoute
          element={<Users />}
          permission="users.read"
        />
      ),
    },

    /*   Roles   */
    {
      path: "/roles",
      element: (
        <ProtectedRoute
          element={<Roles />}
          permission="roles.read"
        />
      ),
    },

    /*   Permissions   */
    {
      path: "/permissions",
      element: (
        <ProtectedRoute
          element={<Permissions />}
          permission="permissions.read"
        />
      ),
    },

    /*   Contact Us   */
    {
      path: "/contact-us",
      element: (
        <ProtectedRoute
          element={<ContactUs />}
        />
      ),
    },

    /*   Privacy Policy   */
    {
      path: "/privacy-policy",
      element: (
        <ProtectedRoute
          element={<PrivacyPolicy />}
        />
      ),
    },

    /*   Error   */
    {
      path: "/auth/404",
      element: <PrivateRouteError />,
    },

    /*   Catch All   */
    {
      path: "*",
      element: (
        <Navigate
          to="/auth/404"
          replace
        />
      ),
    },

  ],
};

/*   Public Routes   */
const publicRoute = {
  path: "/",
  element: <BlankLayout />,

  children: [

    /*   Default Redirect   */
    {
      path: "/",
      element: (
        <Navigate
          to="/auth/login"
          replace
        />
      ),
    },

    /*   Login   */

    {
      path: "/auth/login",
      element: <Login />,
    },

    /*   Privacy Policy   */

    {
      path: "/privacy-policy",
      element: <PrivacyPolicy />,
    },

    /*   Contact Us   */

    {
      path: "/contact-us",
      element: <ContactUs />,
    },

    /*   Catch All   */

    {
      path: "*",
      element: (
        <Navigate
          to="/auth/login"
          replace
        />
      ),
    },

  ],
};

/*   Router   */
const Router = () => {

  const { token } = useSelector(
    (state) => state.auth
  );

  return token
    ? [privateRoute]
    : [publicRoute];
};

export default Router;